import React from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

interface PieChartWithNeedleProps {
  value: number; // 0-100 percentage
  size?: string;
  color?: string;
  thickness?: string;
  fontSize?: string;
}

const RADIAN = Math.PI / 180;

// Needle component
const renderNeedle = (value: number, cx: number, cy: number, iR: number, oR: number, color: string) => {
  // Convert percentage (0-100) to angle (180° to 0°)
  const ang = 180.0 * (1 - value / 100);
  const length = (iR + 2 * oR) / 3;
  const sin = Math.sin(-RADIAN * ang);
  const cos = Math.cos(-RADIAN * ang);
  const r = 5;
  const x0 = cx + 5;
  const y0 = cy + 5;
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  return [
    <circle key="needle-center" cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
    <path key="needle-path" d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`} stroke="#none" fill={color} />,
  ];
};

export const GaugePieChart: React.FC<PieChartWithNeedleProps> = ({ value, size = "300px" }) => {
  const needleColor = useColorModeValue("gray.600", "white");
  const textColor = useColorModeValue("gray.700", "gray.200");

  // Convert Chakra color to hex
  const getColorValue = (chakraColor?: string) => {
    switch (chakraColor) {
      case "green":
        return "#68D391";
      case "blue":
        return "#63B3ED";
      case "red":
        return "#FC8181";
      default:
        return "#9c9c9c";
    }
  };

  const sizeNum = parseInt(size);
  const chartHeight = `${sizeNum * 0.6}px`; // Reduce height to show only semicircle area

  // Data for the pie chart - four quarters with different colors
  const data = [
    { name: "Quarter1", value: 25, fill: getColorValue("red") },
    { name: "Quarter2", value: 25, fill: getColorValue("blue") },
    { name: "Quarter3", value: 25, fill: getColorValue("green") },
    { name: "Quarter4", value: 25, fill: getColorValue() },
  ];

  return (
    <Box position="relative" width={size} height={chartHeight}>
      <Box height={size} overflow="hidden">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              startAngle={180}
              endAngle={0}
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={sizeNum * 0.35}
              innerRadius={sizeNum * 0.25}
              fill="#9c9c9c"
              stroke="none">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            {renderNeedle(value, sizeNum / 2, sizeNum / 2, sizeNum * 0.25, sizeNum * 0.35, needleColor)}
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Scale markers */}
      <Box position="absolute" top="0" left="0" width="100%" height="100%">
        <Text position="absolute" left="16px" top="80%" transform="translateY(-50%)" fontSize="xs" color={textColor}>
          0
        </Text>
        <Text position="absolute" left="45px" top="33%" transform="translateY(-50%)" fontSize="xs" color={textColor}>
          25
        </Text>
        <Text position="absolute" left="50%" top="15px" transform="translateX(-50%)" fontSize="xs" color={textColor}>
          50
        </Text>
        <Text position="absolute" right="45px" top="28%" transform="translateX(-50%)" fontSize="xs" color={textColor}>
          75
        </Text>
        <Text position="absolute" right="10px" top="80%" transform="translateY(-50%)" fontSize="xs" color={textColor}>
          100
        </Text>
      </Box>
    </Box>
  );
};
